import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowLeft, Sparkles, ShieldCheck, XCircle, RotateCcw, Loader2,
  Calendar, CheckCircle2,
} from "lucide-react";

function money(cents, currency = "usd") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase(), maximumFractionDigits: 2 }).format((cents || 0) / 100);
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    if (typeof v === "number") return new Date(v * 1000).toLocaleDateString();
    return new Date(v).toLocaleDateString();
  } catch { return "—"; }
}

/**
 * `/my-plan` — a client's view of their reseller-plan subscription.
 * Only meaningful for teacher/parent accounts that subscribed via `/r/{slug}`.
 * Everyone else sees a friendly empty state pointing them at `/subscription`.
 */
export default function MyPlan() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setSub((await api.myResellerPlan()).subscription); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current period.")) return;
    setBusy(true);
    try { await api.cancelMyResellerPlan(); toast.success("Subscription will end at the period end."); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await api.resumeMyResellerPlan(); toast.success("Subscription resumed — you're set."); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>
  );

  if (!sub) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-[#0D9488] hover:underline"><ArrowLeft size={14} /> Back</Link>
        <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-8 text-center" data-testid="my-plan-empty">
          <Sparkles size={26} className="mx-auto text-[#94A3B8]" />
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-[#1F2937]">No partner subscription yet</h1>
          <p className="mt-2 font-body text-sm text-[#6B7280]">
            You subscribed through ZoodLeap directly, or you used a license code from a partner. There's nothing to manage on this page.
          </p>
          <Link to="/subscription" className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-5 py-3 font-heading text-sm font-bold text-white hover:brightness-95">Manage ZoodLeap subscription</Link>
        </div>
      </div>
    );
  }

  const { plan, brand, status } = sub;
  const accent = brand?.accent_color || "#0D9488";
  const isCancelling = status === "cancelling" || sub.cancel_at_period_end;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="my-plan-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-[#0D9488] hover:underline"><ArrowLeft size={14} /> Back</Link>

      <div className="mt-4 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl text-white" style={{ background: accent }}>
          {brand?.logo_url ? <img src={brand.logo_url} alt="" className="h-full w-full rounded-2xl object-cover" /> : <Sparkles size={20} />}
        </div>
        <div>
          <div className="font-body text-[10px] uppercase tracking-widest font-bold text-[#94A3B8]">Your subscription with</div>
          <div className="font-heading text-lg font-extrabold text-[#1F2937]" data-testid="my-plan-brand">{brand?.name || "your partner"}</div>
        </div>
      </div>

      <div className="mt-5 rounded-3xl border-2 bg-white p-6" style={{ borderColor: `${accent}33` }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-heading text-2xl font-extrabold text-[#1F2937]" data-testid="my-plan-name">{plan?.name}</div>
            {plan?.description && <p className="mt-1 font-body text-sm text-[#6B7280]">{plan.description}</p>}
          </div>
          {isCancelling ? (
            <span className="rounded-full bg-[#F59E0B]/15 px-3 py-1 text-xs font-bold text-[#B45309]" data-testid="my-plan-status">Cancelling</span>
          ) : (
            <span className="rounded-full bg-[#10B981]/15 px-3 py-1 text-xs font-bold text-[#047857]" data-testid="my-plan-status">Active</span>
          )}
        </div>
        <div className="mt-4">
          <span className="font-heading text-4xl font-extrabold" style={{ color: accent }} data-testid="my-plan-price">{money(plan?.amount_cents, plan?.currency)}</span>
          <span className="ml-1 font-body text-sm text-[#6B7280]">/{plan?.interval === "year" ? "yr" : "mo"}</span>
        </div>

        <div className="mt-4 space-y-2" data-testid="my-plan-features">
          <div className="flex items-start gap-2 font-body text-sm text-[#1F2937]">
            <CheckCircle2 size={16} style={{ color: accent }} className="mt-0.5 shrink-0" /> <span>Up to {plan?.max_students || 30} {(plan?.max_students || 30) <= 10 ? "children" : "students"}</span>
          </div>
          {(plan?.features || []).map((f) => (
            <div key={f} className="flex items-start gap-2 font-body text-sm text-[#1F2937]">
              <CheckCircle2 size={16} style={{ color: accent }} className="mt-0.5 shrink-0" /> <span>{f}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 grid gap-2 rounded-2xl bg-[#FAF7F2] p-4 text-sm text-[#6B7280] sm:grid-cols-2">
          <div className="flex items-center gap-2"><Calendar size={14} /> Activated <b className="text-[#1F2937]">{fmtDate(sub.activated_at)}</b></div>
          {sub.current_period_end && (
            <div className="flex items-center gap-2"><Calendar size={14} /> {isCancelling ? "Access ends" : "Next renewal"} <b className="text-[#1F2937]">{fmtDate(sub.current_period_end)}</b></div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {isCancelling ? (
            <button onClick={resume} disabled={busy} data-testid="my-plan-resume"
              className="inline-flex items-center gap-2 rounded-2xl bg-[#10B981] px-5 py-3 font-heading text-sm font-bold text-white hover:brightness-95 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Resume subscription
            </button>
          ) : (
            <button onClick={cancel} disabled={busy} data-testid="my-plan-cancel"
              className="inline-flex items-center gap-2 rounded-2xl border-2 border-[#E05A47] bg-white px-5 py-3 font-heading text-sm font-bold text-[#E05A47] hover:bg-[#E05A47] hover:text-white disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Cancel subscription
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-2xl bg-[#F2ECE1]/50 p-4 text-xs text-[#6B7280]" data-testid="my-plan-note">
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#0D9488]" />
        <span>
          Payments are processed by {brand?.name}. You'll see the charge from their business name on your statement. To update your card, use the receipt email link from your last invoice.
        </span>
      </div>
    </div>
  );
}
